import {EmptyPolicyNumber, PolicyNumber, ValidPolicyNumber} from 'ApplicationEntry/models/EnteredPolicyNumber';
import {EmptyString, isNonEmptyString, NonEmptyString, StringUnion} from 'Core/models/EmptyString';
import * as I from 'immutable';
import {ToPartialMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';

type ULProps = {
  plan: StringUnion;
  premium: StringUnion;
  calculatedPremium: StringUnion;
  cwa: StringUnion;
  situsState: StringUnion;
  type: StringUnion;
  faceAmt: StringUnion;
  policyNumber: PolicyNumber;
  premiumType: StringUnion;
  priInsured: StringUnion;
  spouseInsured: StringUnion;
  autoIssueEligibility: boolean;
  replacement: boolean;
  riderCodes: I.List<string>;
  riderAmounts: I.List<number>;
  commission: StringUnion;
  deathBenefit: StringUnion;
  adb: boolean;
  sbVolume: StringUnion;
  waiver: boolean;
  strikeLayOff: boolean;
  guac: boolean;
  chronicIllness: StringUnion;
};

export class InvalidOrdUL {
  public readonly key = 'InvalidOrdUL';

  constructor(
    public readonly plan: StringUnion,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly cwa: StringUnion,
    public readonly situsState: StringUnion,
    public readonly type: StringUnion,
    public readonly faceAmt: StringUnion,
    public readonly policyNumber: PolicyNumber,
    public readonly premiumType: StringUnion,
    public readonly priInsured: StringUnion,
    public readonly spouseInsured: StringUnion,
    public readonly autoIssueEligibility: boolean,
    public readonly replacement: boolean,
    public readonly riderCodes: I.List<string>,
    public readonly riderAmounts: I.List<number>,
    public readonly commission: StringUnion,
    public readonly deathBenefit: StringUnion,
    public readonly adb: boolean,
    public readonly sbVolume: StringUnion,
    public readonly waiver: boolean,
    public readonly strikeLayOff: boolean,
    public readonly guac: boolean,
    public readonly chronicIllness: StringUnion
  ) {}
}

export class ValidOrdUL {
  public static createApp({
    plan,
    premium,
    calculatedPremium,
    cwa,
    situsState,
    type,
    faceAmt,
    policyNumber,
    premiumType,
    priInsured,
    spouseInsured,
    autoIssueEligibility,
    replacement,
    riderCodes,
    riderAmounts,
    commission,
    deathBenefit,
    adb,
    sbVolume,
    waiver,
    strikeLayOff,
    guac,
    chronicIllness,
  }: ULProps) {
    return this.create(
      plan,
      premium,
      calculatedPremium,
      cwa,
      situsState,
      type,
      faceAmt,
      policyNumber,
      premiumType,
      priInsured,
      spouseInsured,
      autoIssueEligibility,
      replacement,
      riderCodes,
      riderAmounts,
      commission,
      deathBenefit,
      adb,
      sbVolume,
      waiver,
      strikeLayOff,
      guac,
      chronicIllness
    );
  }

  public static create(
    plan: StringUnion,
    premium: StringUnion,
    calculatedPremium: StringUnion,
    cwa: StringUnion,
    situsState: StringUnion,
    type: StringUnion,
    faceAmt: StringUnion,
    policyNumber: PolicyNumber,
    premiumType: StringUnion,
    priInsured: StringUnion,
    spouseInsured: StringUnion,
    autoIssueEligibility: boolean,
    replacement: boolean,
    riderCodes: I.List<string>,
    riderAmounts: I.List<number>,
    commission: StringUnion,
    deathBenefit: StringUnion,
    adb: boolean,
    sbVolume: StringUnion,
    waiver: boolean,
    strikeLayOff: boolean,
    guac: boolean,
    chronicIllness: StringUnion
  ) {
    return isNonEmptyString(plan)
      ? new ValidOrdUL(
          plan,
          premium,
          calculatedPremium,
          cwa,
          situsState,
          type,
          faceAmt,
          policyNumber,
          premiumType,
          priInsured,
          spouseInsured,
          autoIssueEligibility,
          replacement,
          riderCodes,
          riderAmounts,
          commission,
          deathBenefit,
          adb,
          sbVolume,
          waiver,
          strikeLayOff,
          guac,
          chronicIllness
        )
      : new InvalidOrdUL(
          plan,
          premium,
          calculatedPremium,
          cwa,
          situsState,
          type,
          faceAmt,
          policyNumber,
          premiumType,
          priInsured,
          spouseInsured,
          autoIssueEligibility,
          replacement,
          riderCodes,
          riderAmounts,
          commission,
          deathBenefit,
          adb,
          sbVolume,
          waiver,
          strikeLayOff,
          guac,
          chronicIllness
        );
  }

  public readonly key = 'ValidOrdUL';
  constructor(
    public readonly plan: StringUnion,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly cwa: StringUnion,
    public readonly situsState: StringUnion,
    public readonly type: StringUnion,
    public readonly faceAmt: StringUnion,
    public readonly policyNumber: PolicyNumber,
    public readonly premiumType: StringUnion,
    public readonly priInsured: StringUnion,
    public readonly spouseInsured: StringUnion,
    public readonly autoIssueEligibility: boolean,
    public readonly replacement: boolean,
    public readonly riderCodes: I.List<string>,
    public readonly riderAmounts: I.List<number>,
    public readonly commission: StringUnion,
    public readonly deathBenefit: StringUnion,
    public readonly adb: boolean,
    public readonly sbVolume: StringUnion,
    public readonly waiver: boolean,
    public readonly strikeLayOff: boolean,
    public readonly guac: boolean,
    public readonly chronicIllness: StringUnion
  ) {}
}

export type OrdUL = InvalidOrdUL | ValidOrdUL;

export type OrdULOptions<TReturn> = ToPartialMatchOptions<OrdUL, 'key', TReturn>;

export const matchOrdUL = <TReturn>(options: OrdULOptions<TReturn>) => (app: OrdUL): TReturn => {
  switch (app.key) {
    case 'InvalidOrdUL':
      return options.InvalidOrdUL ? options.InvalidOrdUL(app) : options.default(app);
    case 'ValidOrdUL':
      return options.ValidOrdUL ? options.ValidOrdUL(app) : options.default(app);
  }
};

export const EmptyOrdUL = new InvalidOrdUL(
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  EmptyPolicyNumber,
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  false,
  false,
  I.List<string>(),
  I.List<number>(),
  new EmptyString(),
  new EmptyString(),
  false,
  new EmptyString(),
  false,
  false,
  false,
  new EmptyString()
);

export const changePlan = (plan: string) => (app: OrdUL) =>
  ValidOrdUL.createApp({
    ...app,
    plan: NonEmptyString.create(plan),
    riderCodes: I.List<string>(),
    riderAmounts: I.List<number>(),
  });
export const changePremium = (premium: string) => (app: OrdUL) =>
  ValidOrdUL.createApp({...app, premium: NonEmptyString.create(premium)});
export const changeCalculatedPremium = (calculatedPremium: string) => (app: OrdUL) =>
  ValidOrdUL.createApp({...app, calculatedPremium: NonEmptyString.create(calculatedPremium)});
export const changeCWA = (cwa: string) => (app: OrdUL) =>
  ValidOrdUL.createApp({...app, cwa: NonEmptyString.create(cwa)});
export const changeSitusState = (situsState: string) => (app: OrdUL) =>
  ValidOrdUL.createApp({...app, situsState: NonEmptyString.create(situsState)});
export const changeType = (type: string) => (app: OrdUL) =>
  ValidOrdUL.createApp({...app, type: NonEmptyString.create(type)});
export const changeFaceAmt = (faceAmt: string) => (app: OrdUL) =>
  ValidOrdUL.createApp({...app, faceAmt: NonEmptyString.create(faceAmt)});
export const changePolicyNumber = (polNum: string) => (app: OrdUL) =>
  ValidOrdUL.createApp({...app, policyNumber: ValidPolicyNumber.create(NonEmptyString.create(polNum))});
export const changePremiumType = (preType: string) => (app: OrdUL) =>
  ValidOrdUL.createApp({...app, premiumType: NonEmptyString.create(preType)});
export const changePriInsured = (priInsured: string) => (app: OrdUL) =>
  ValidOrdUL.createApp({...app, priInsured: NonEmptyString.create(priInsured)});
export const changeSpouseInsured = (spouseInsured: string) => (app: OrdUL) =>
  ValidOrdUL.createApp({...app, spouseInsured: NonEmptyString.create(spouseInsured)});
export const changeAutoIssue = (autoIssueEligibility: boolean) => (app: OrdUL) =>
  ValidOrdUL.createApp({...app, autoIssueEligibility});
export const changeReplacement = (replacement: boolean) => (app: OrdUL) =>
  ValidOrdUL.createApp({...app, replacement, commission: new EmptyString()});
export const changeCommision = (commission: string) => (app: OrdUL) =>
  ValidOrdUL.createApp({...app, commission: NonEmptyString.create(commission)});
export const changeDeathBenefit = (deathBenefit: string) => (app: OrdUL) =>
  ValidOrdUL.createApp({...app, deathBenefit: NonEmptyString.create(deathBenefit)});
export const changeSBVolume = (vol: string) => (app: OrdUL) =>
  ValidOrdUL.createApp({...app, sbVolume: NonEmptyString.create(vol)});
export const changeChronicIllness = (illness: string) => (app: OrdUL) =>
  ValidOrdUL.createApp({...app, chronicIllness: NonEmptyString.create(illness)});
export const changeADB = (adb: boolean) => (app: OrdUL) =>
  ValidOrdUL.createApp({...app, adb, sbVolume: new EmptyString()});
export const changeWaiver = (waiver: boolean) => (app: OrdUL) => ValidOrdUL.createApp({...app, waiver});
export const changeStrikeLayOff = (strikeLayOff: boolean) => (app: OrdUL) =>
  ValidOrdUL.createApp({...app, strikeLayOff});
export const changeGUAC = (guac: boolean) => (app: OrdUL) =>
  ValidOrdUL.createApp({...app, guac, chronicIllness: new EmptyString()});

export const changeRiderCodes = (riderCode: string, index: number) => (app: OrdUL) => {
  const existingList: I.List<string> = app.riderCodes;
  const newList = existingList.set(index, riderCode);
  return ValidOrdUL.createApp({...app, riderCodes: newList});
};

export const changeRiderAmount = (riderAmt: number, index: number) => (app: OrdUL) => {
  const existingList: I.List<number> = app.riderAmounts;
  const newList = existingList.set(index, riderAmt);

  return ValidOrdUL.createApp({...app, riderAmounts: newList});
};
